/**
 * WebAuthn Authentication Service
 * Handles passkey-based authentication flow
 */
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialRequestOptionsJSON,
  PublicKeyCredentialCreationOptionsJSON,
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/types';

import { getApiBase } from './apiBase';

const API_BASE = getApiBase();

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'reception' | 'workshop';
  hasPassword?: boolean;
  passkeyCount?: number;
}

export interface AccountLockedError {
  error: 'account_locked';
  message: string;
  lockedUntil: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export class AuthError extends Error {
  constructor(
    message: string,
    public code: string = 'AUTH_ERROR',
    public lockedUntil?: string
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Login with password
 */
export async function loginWithPassword(email: string, password: string): Promise<User> {
  const response = await fetch(`${API_BASE}/api/auth/login/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  // Handle account locked error (423 status)
  if (response.status === 423 && data.error === 'account_locked') {
    throw new AuthError(
      data.message || 'Cuenta bloqueada temporalmente',
      'ACCOUNT_LOCKED',
      data.lockedUntil
    );
  }

  if (!response.ok || !data.success || !data.data?.user) {
    throw new AuthError(
      data.error?.message || data.message || 'Email o contrasena incorrectos',
      data.error?.code || 'LOGIN_FAILED'
    );
  }

  return data.data.user;
}

/**
 * Login with passkey
 */
export async function loginWithPasskey(email: string): Promise<User> {
  // Step 1: Get authentication options from server
  const startResponse = await fetch(`${API_BASE}/api/auth/login/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  const startData: ApiResponse<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }> = await startResponse.json();

  if (!startResponse.ok || !startData.success || !startData.data) {
    throw new AuthError(
      startData.error?.message || 'Error al iniciar autenticación',
      startData.error?.code || 'LOGIN_START_FAILED'
    );
  }

  const { challengeId, options } = startData.data;

  // Step 2: Trigger WebAuthn authentication
  let credential: AuthenticationResponseJSON;
  try {
    credential = await startAuthentication({ optionsJSON: options });
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'NotAllowedError') {
        throw new AuthError('Autenticación cancelada por el usuario', 'USER_CANCELLED');
      }
      if (err.name === 'SecurityError') {
        throw new AuthError('Error de seguridad: dominio no válido', 'SECURITY_ERROR');
      }
      throw new AuthError(`Error WebAuthn: ${err.message}`, 'WEBAUTHN_ERROR');
    }
    throw new AuthError('Error desconocido en WebAuthn', 'UNKNOWN_WEBAUTHN_ERROR');
  }

  // Step 3: Verify credential with server
  const finishResponse = await fetch(`${API_BASE}/api/auth/login/finish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Challenge-ID': challengeId,
    },
    credentials: 'include',
    body: JSON.stringify({ response: credential }),
  });

  const finishData: ApiResponse<{ user: User }> = await finishResponse.json();

  if (!finishResponse.ok || !finishData.success || !finishData.data) {
    throw new AuthError(
      finishData.error?.message || 'Error al verificar credencial',
      finishData.error?.code || 'LOGIN_FINISH_FAILED'
    );
  }

  return finishData.data.user;
}

/**
 * Register a new passkey
 */
export async function registerPasskey(email: string): Promise<void> {
  // Step 1: Get registration options from server
  const startResponse = await fetch(`${API_BASE}/api/auth/register/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  const startData: ApiResponse<{
    challengeId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }> = await startResponse.json();

  if (!startResponse.ok || !startData.success || !startData.data) {
    throw new AuthError(
      startData.error?.message || 'Error al iniciar registro',
      startData.error?.code || 'REGISTER_START_FAILED'
    );
  }

  const { challengeId, options } = startData.data;

  // Step 2: Trigger WebAuthn registration
  let credential: RegistrationResponseJSON;
  try {
    credential = await startRegistration({ optionsJSON: options });
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'NotAllowedError') {
        throw new AuthError('Registro cancelado por el usuario', 'USER_CANCELLED');
      }
      if (err.name === 'InvalidStateError') {
        throw new AuthError('Este dispositivo ya tiene una passkey registrada', 'ALREADY_REGISTERED');
      }
      throw new AuthError(`Error WebAuthn: ${err.message}`, 'WEBAUTHN_ERROR');
    }
    throw new AuthError('Error desconocido en WebAuthn', 'UNKNOWN_WEBAUTHN_ERROR');
  }

  // Step 3: Verify registration with server
  const finishResponse = await fetch(`${API_BASE}/api/auth/register/finish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Challenge-ID': challengeId,
    },
    credentials: 'include',
    body: JSON.stringify({ email, response: credential }),
  });

  const finishData: ApiResponse<{ message: string }> = await finishResponse.json();

  if (!finishResponse.ok || !finishData.success) {
    throw new AuthError(
      finishData.error?.message || 'Error al verificar registro',
      finishData.error?.code || 'REGISTER_FINISH_FAILED'
    );
  }
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetch(`${API_BASE}/api/auth/me`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data: ApiResponse<{ user: User }> = await response.json();
    return data.success && data.data?.user ? data.data.user : null;
  } catch {
    return null;
  }
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

/**
 * Request password reset email
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/auth/password/forgot`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new AuthError(
      data.error?.message || 'Error al solicitar restablecimiento',
      data.error?.code || 'RESET_REQUEST_FAILED'
    );
  }
}

/**
 * Reset password with token
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/auth/password/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, newPassword }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new AuthError(
      data.error?.message || data.message || 'Error al restablecer contrasena',
      data.error?.code || 'RESET_FAILED'
    );
  }
}
